import axios from "axios";

export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_URL
});

const pendingRequests = new Map();

// Utility function to create a unique request identifier
const createRequestIdentifier = (config) => {
  const { url, method, params } = config;
  // Create identifier based on method and URL with query parameters
  const paramStr = params ? JSON.stringify(params) : "";
  const queryString = url.includes("?") ? url.split("?")[1] : "";
  return `${method.toUpperCase()}_${
    url.split("?")[0]
  }_${queryString}_${paramStr}`;
};

// Request Interceptor - Only cancel truly duplicate requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Only apply duplicate cancellation if no cancel token is already provided
    // This allows components to manage their own cancellation strategy
    if (!config.cancelToken) {
      const requestIdentifier = createRequestIdentifier(config);
      console.log("Checking for duplicate request:", requestIdentifier);

      // Check if there is already a pending request with the same identifier
      if (pendingRequests.has(requestIdentifier)) {
        const existingCancelSource = pendingRequests.get(requestIdentifier);
        // Cancel the previous identical request
        existingCancelSource.cancel(
          `Duplicate request cancelled: ${requestIdentifier}`
        );
        console.log("Cancelled duplicate request:", requestIdentifier);
      }

      // Create a new cancel token for this request
      const newCancelTokenSource = axios.CancelToken.source();
      config.cancelToken = newCancelTokenSource.token;
      pendingRequests.set(requestIdentifier, newCancelTokenSource);
    } else {
      console.log(
        "Request has existing cancel token, skipping duplicate check"
      );
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Clean up completed request from pending map
    const requestIdentifier = createRequestIdentifier(response.config);
    pendingRequests.delete(requestIdentifier);
    console.log("Request completed:", requestIdentifier);
    return response;
  },
  (error) => {
    // Clean up failed/cancelled request from pending map
    if (error.config) {
      const requestIdentifier = createRequestIdentifier(error.config);
      pendingRequests.delete(requestIdentifier);

      if (axios.isCancel(error)) {
        console.log("Request cancelled:", requestIdentifier, error.message);
      } else {
        console.error("Request failed:", requestIdentifier, error.message);
      }
    }

    return Promise.reject(error);
  }
);

// Get count of pending requests (useful for debugging)
export const getPendingRequestCount = () => {
  return pendingRequests.size;
};

// Get list of pending request identifiers (useful for debugging)
export const getPendingRequests = () => {
  return Array.from(pendingRequests.keys());
};
